import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppointmentService } from '../../core/services/appointment.service';
import { AuthService } from '../../core/services/auth.service';
import { Appointment, TimeSlot } from '../../core/models/appointment.model';
import { User } from '../../core/models/user.model';
import { MOCK_USERS, SERVICE_TYPES } from '../../core/mock/mock-data';

type Step = 'select' | 'confirm' | 'success';

@Component({
  selector: 'app-public-booking',
  standalone: false,
  templateUrl: './public-booking.component.html',
  styleUrl: './public-booking.component.scss',
})
export class PublicBookingComponent implements OnInit, OnDestroy {
  professional: User | null = null;
  notFound = false;

  step: Step = 'select';

  // Selección
  availableDates: string[] = [];
  selectedDate = '';
  timeSlots: TimeSlot[] = [];
  selectedTime = '';
  serviceTypes = SERVICE_TYPES;
  selectedService = SERVICE_TYPES[0];

  // Datos del cliente (gesta como invitado)
  clientName = '';
  clientEmail = '';
  notes = '';

  isLoading = false;
  error = '';
  createdAppointment: Appointment | null = null;

  private sub = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apptSvc: AppointmentService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    const proId = this.route.snapshot.paramMap.get('professionalId');
    this.professional = MOCK_USERS.find(u => u.id === proId && u.role === 'professional') ?? null;
    if (!this.professional) { this.notFound = true; return; }

    this.availableDates = this.apptSvc.getAvailableDates(30);
    if (this.availableDates.length) this.selectDate(this.availableDates[0]);

    // Pre-rellenar si hay sesión
    const loggedUser = this.auth.currentUserSnapshot;
    if (loggedUser) {
      this.clientName  = loggedUser.name;
      this.clientEmail = loggedUser.email;
    }
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }

  selectDate(date: string): void {
    this.selectedDate = date;
    this.selectedTime = '';
    this.timeSlots = this.apptSvc.slotsForDate(this.professional!.id, date);
  }

  selectTime(slot: TimeSlot): void {
    if (!slot.available) return;
    this.selectedTime = slot.time;
  }

  selectService(svc: typeof SERVICE_TYPES[0]): void {
    this.selectedService = svc;
  }

  get canProceed(): boolean {
    return !!this.selectedDate && !!this.selectedTime;
  }

  get canSubmit(): boolean {
    return !!this.clientName.trim() && !!this.clientEmail.trim() && !this.isLoading;
  }

  goToConfirm(): void {
    if (this.canProceed) this.step = 'confirm';
  }

  backToSelect(): void {
    this.step = 'select';
  }

  submit(): void {
    if (!this.canSubmit || !this.professional) return;
    this.isLoading = true;
    this.error = '';

    setTimeout(() => {
      const loggedUser = this.auth.currentUserSnapshot;
      this.createdAppointment = this.apptSvc.create({
        clientId:         loggedUser?.id ?? 'guest-' + Date.now(),
        clientName:       this.clientName.trim(),
        clientEmail:      this.clientEmail.trim(),
        professionalId:   this.professional!.id,
        professionalName: this.professional!.name,
        date:             this.selectedDate,
        time:             this.selectedTime,
        duration:         this.selectedService.duration,
        serviceType:      this.selectedService.label,
        notes:            this.notes.trim() || undefined,
      });
      this.isLoading = false;
      this.step = 'success';
    }, 700);
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-AR', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  }

  formatDateFull(dateStr: string): string {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-AR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  copyLink(): void {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
  }
}
